import React, { useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import PageWrapper from "../../../components/layout/page/PageWrapper";
import PageHeader from "../../../components/layout/page/PageHeader";
import ViewContainer from "../../../components/layout/view/ViewContainer";
import ViewHeader from "../../../components/layout/view/ViewHeader";

import SecondaryPillTabs from "../../../components/tabs/SecondaryPillTabs";
import Button from "../../../components/buttons/Button";
import EmptyState from "../../../components/feedback/EmptyState";
import ContentGrid from "../../../components/grids/content/ContentGrid";
import VideoThumbnailCard from "../../../components/cards/thumbnails/VideoThumbnailCard";

import { useMyVideoRentals } from "../../videos/users/hooks/useVideoRental";
import { organizeRentals } from "../utils/rentalUtils";

import "./MyRentalsPage.css";

const TABS = [
  { id: "active", label: "Active" },
  { id: "expired", label: "Expired" },
];

const GRID_CONFIG = {
  columns: 4,
  tabletColumns: 3,
  mobileColumns: 2,
  gap: 16,
  showEmpty: false,
};

export default function MyRentalsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { rentals = [], loading, error, refreshRentals } = useMyVideoRentals();

  const activeTab =
    searchParams.get("tab") === "expired" ? "expired" : "active";

  const { activeVideos, expiredVideos } = useMemo(
    () => organizeRentals(rentals),
    [rentals],
  );

  const tabs = [
    { id: "active", label: `Active (${activeVideos.length})` },
    { id: "expired", label: `Expired (${expiredVideos.length})` },
  ];

  const videos = activeTab === "active" ? activeVideos : expiredVideos;

  const handleTabChange = (selected) => {
    const tab =
      typeof selected === "string" ? selected : selected?.id || selected?.value;

    if (!TABS.some((item) => item.id === tab)) return;

    setSearchParams((previous) => {
      const params = new URLSearchParams(previous);
      params.set("tab", tab);
      return params;
    });
  };

  const handleVideoClick = (video) => {
    if (!video?.videoTag) return;

    navigate(`/watch/${encodeURIComponent(video.videoTag)}`);
  };

  const handleRefresh = () => {
    if (!loading) {
      void refreshRentals();
    }
  };

  const renderVideo = (video) => (
    <VideoThumbnailCard
      key={video.videoId || video.videoTag}
      video={video}
      onClick={() => handleVideoClick(video)}
    />
  );

  return (
    <PageWrapper>
      <PageHeader
        title="My Rentals"
        description="Watch your rented videos and manage your viewing access."
      />

      <ViewContainer>
        <ViewHeader
          title="My Videos"
          description="Your active and previous video rentals."
        />

        <SecondaryPillTabs
          tabs={tabs}
          activeTab={activeTab}
          onChange={handleTabChange}
        />

        {error && (
          <div className="my-rentals__error" role="alert">
            <p>
              {typeof error === "string"
                ? error
                : error.message || "Unable to load rentals."}
            </p>

            <Button
              value="Try Again"
              variant="primary-outlined"
              onClick={handleRefresh}
              disabled={loading}
            />
          </div>
        )}

        {loading && videos.length === 0 ? (
          <ContentGrid
            {...GRID_CONFIG}
            items={[]}
            loading
            skeletonCount={8}
            renderItem={() => null}
          />
        ) : videos.length === 0 && !error ? (
          <EmptyState
            title={
              activeTab === "active"
                ? "No active rentals"
                : "No expired rentals"
            }
            description={
              activeTab === "active"
                ? "Your active video rentals will appear here."
                : "Your previous rentals will appear here."
            }
          />
        ) : (
          <ContentGrid
            {...GRID_CONFIG}
            items={videos}
            renderItem={renderVideo}
          />
        )}
      </ViewContainer>
    </PageWrapper>
  );
}
